import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ModifyReservationModal from './ModifyReservationModal';
import Swal from 'sweetalert2';
import ReservationChat from './ReservationChat';

const BookingDetailView = ({ booking, open, onClose, getBookingTotal, userPermissions = [] }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  const effectivePermissions = isAdmin
    ? ['modifyBooking', 'cancelBooking', 'noShowBooking']
    : userPermissions;
  const isPrinting = typeof window !== 'undefined' && window.matchMedia('print').matches;
  const [expandedRooms, setExpandedRooms] = useState([]);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const printRef = useRef();

  // Inject global print styles once
  if (typeof document !== 'undefined' && !document.getElementById('modal-print-style')) {
    const style = document.createElement('style');
    style.id = 'modal-print-style';
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        .printable-content, .printable-content * {
          visibility: visible;
        }
        .printable-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          background: white;
          padding: 20px;
        }
        button, .MuiIconButton-root {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  const handleNoShow = async () => {
    const confirm = await Swal.fire({
      title: 'Mark as No Show?',
      text: 'This action will update the booking status.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, mark as no show',
      cancelButtonText: 'Cancel'
    });

    if (confirm.isConfirmed) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/no-show/${booking._id}`, {
          method: 'PUT',
        });
        const result = await response.json();
        if (result.success) {
          Swal.fire({
            icon: 'success',
            title: 'Marked as No Show',
            text: 'The booking status has been updated.',
            confirmButtonColor: '#A58E63'
          }).then(() => window.location.reload());
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Action failed',
            text: result.message || 'An unknown error occurred.',
            confirmButtonColor: '#A58E63'
          });
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Network error',
          text: err.message,
          confirmButtonColor: '#A58E63'
        });
      }
    }
  };

  if (!booking) return null;

  const handlePrint = () => {
    const originalModal = document.querySelector('.MuiModal-root');
    if (originalModal) originalModal.style.display = 'none';

    const printDiv = document.createElement('div');
    printDiv.innerHTML = printRef.current.innerHTML;
    printDiv.className = 'printable-content';
    document.body.appendChild(printDiv);

    window.print();

    document.body.removeChild(printDiv);
    if (originalModal) originalModal.style.display = '';
  };

  const handleCancel = async () => {
    const confirm = await Swal.fire({
      title: 'Cancel this reservation?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'Keep reservation'
    });

    if (confirm.isConfirmed) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/cancel/${booking._id}`, {
          method: 'PUT',
        });
        const result = await response.json();
        if (result.success) {
          Swal.fire({
            icon: 'success',
            title: 'Reservation cancelled',
            text: 'The booking has been marked as cancelled.',
            confirmButtonColor: '#A58E63'
          }).then(() => window.location.reload());
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Cancel failed',
            text: result.message || 'An unknown error occurred.',
            confirmButtonColor: '#A58E63'
          });
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Network error',
          text: err.message,
          confirmButtonColor: '#A58E63'
        });
      }
    }
  };

  return (
    <Box
      ref={printRef}
      className="printable-content"
      sx={{
        width: '100%',
        maxWidth: 1150,
        margin: '40px auto', // updated margin
        bgcolor: 'transparent',
        borderRadius: 2,
        boxShadow: 'none',
        p: 0,
      }}
    >
      <Box display="flex" gap={3} flexWrap="wrap">
        {/* LEFT COLUMN: Booking Details */}
        <Box
          flex="2"
          minWidth="350px"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 3,
            p: 4,
            border:
              booking.status === 'cancelled' ? '2px solid #f44336' :
              booking.status === 'no-show' ? '2px solid #ff9800' : undefined
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Reservation details</Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={3}>
            <Box flex="1 1 40%">
              <Typography><strong>Check-in:</strong><br />{new Date(booking.checkIn).toDateString()}</Typography>
              <Typography mt={2}><strong>Check-out:</strong><br />{new Date(booking.checkOut).toDateString()}</Typography>
              <Typography mt={2}><strong>Length of stay:</strong><br />{booking.nights} night(s)</Typography>
              <Typography mt={2}><strong>Total guests:</strong><br />{booking.numberOfGuests}</Typography>
              <Typography mt={2}><strong>Total units:</strong><br />{booking.numberOfRooms}</Typography>
            </Box>

            <Box flex="1 1 55%">
              <Typography><strong>Guest name:</strong><br />{booking.fullName}</Typography>
              <Typography mt={2}><strong>Email:</strong><br />{booking.email}</Typography>
              <Typography mt={2}><strong>Phone:</strong><br />{booking.phone}</Typography>
              <Typography mt={2}><strong>Special request:</strong><br />{booking.specialRequest || '-'}</Typography>
              <Typography mt={2}><strong>Estimated arrival:</strong><br />{booking.estimatedArrivalTime || '-'}</Typography>
            </Box>
          </Box>

          <Box mt={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Box>
              <Typography><strong>Booking number:</strong> {booking.referenceNumber}</Typography>
              <Typography mt={1}>
                <strong>Status:</strong>{' '}
                <span style={{
                  color:
                    booking.status === 'cancelled' ? '#f44336' :
                    booking.status === 'no-show' ? '#ff9800' : 'inherit'
                }}>
                  {booking.status}
                </span>
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography><strong>Total price:</strong></Typography>
              <Typography variant="h6">{getBookingTotal(booking)}</Typography>
            </Box>
          </Box>

          <Box mt={4}>
            <Typography variant="h6" gutterBottom>Rooms</Typography>
            {booking.rooms.map((room, index) => {
              const isExpanded = (typeof window !== 'undefined' && window.matchMedia('print').matches) || expandedRooms.includes(index);
              return (
                <Box key={index} mb={2} p={2} border="1px solid #ccc" borderRadius={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography><strong>{room.roomType}</strong> ({room.bedCountLabel})</Typography>
                    <Typography>${room.totalAfterTax?.toFixed(2)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={1} alignItems="center">
                    <Typography fontSize="0.875rem" color="text.secondary">{room.breakfastIncluded ? 'Breakfast included' : 'No breakfast'}</Typography>
                    <button
                      onClick={() => {
                        if (expandedRooms.includes(index)) {
                          setExpandedRooms(expandedRooms.filter(i => i !== index));
                        } else {
                          setExpandedRooms([...expandedRooms, index]);
                        }
                      }}
                      style={{ fontSize: '0.8rem', background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer' }}
                    >
                      {isExpanded ? 'Hide' : 'Show'} details
                    </button>
                  </Box>
                  {isExpanded && (
                    <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                      <Box flex="1 1 160px">
                        <img
                          src={room.image}
                          alt={room.roomType}
                          style={{ width: '100%', height: 'auto', borderRadius: '6px', objectFit: 'cover' }}
                        />
                      </Box>
                      <Box flex="1 1 60%" pl={10}>
                        <Typography variant="body2"><strong>Rate plan:</strong> {room.ratePlanId}</Typography>
                        <Typography variant="body2" mt={1}><strong>Occupancy:</strong> {`${room.capacity.maxAdults} adults, ${room.capacity.maxChildren} children`}</Typography>
                        <Typography variant="body2" mt={1}><strong>Meal options:</strong> {room.breakfastIncluded ? 'Breakfast included in rate' : 'No breakfast included'}</Typography>
                        <Typography variant="body2" mt={2}><strong>Price per night:</strong></Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
                          {room.perNightPrices?.map((price, i) => (
                            <li key={i}>
                              <Typography variant="body2" component="span">Night {i + 1}: ${price}</Typography>
                            </li>
                          ))}
                        </Box>
                        <Typography variant="body2" mt={2}><strong>VAT:</strong> {room.vat}%</Typography>
                        <Typography variant="body2"><strong>Taxes & Fees:</strong> ${room.taxesAndFees?.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <Box mt={4}>
        <ReservationChat
          reservationId={booking._id}
          guestEmail={booking.email}
          propertyId={booking.propertyId}
           sender="hotel"
        />
        
      </Box>
        </Box>

        {/* RIGHT COLUMN: Actions */}
        <Box
          flex="1"
          minWidth="250px"
          bgcolor="background.paper"
          p={2}
          borderRadius={2}
          boxShadow={3}
          sx={{
            alignSelf: 'flex-start',
            mt: { xs: 3, md: 0 }
          }}
        >
          <Typography variant="subtitle1" mb={2}><strong>Actions</strong></Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <button
              onClick={handlePrint}
              style={{
                width: '100%',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #444',
                color: '#444',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Print
            </button>
            {(booking.status !== 'cancelled' && booking.status !== 'no-show') && (
              <>
                {effectivePermissions.includes('noShowBooking') && (
                  <button
                    onClick={handleNoShow}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      backgroundColor: '#ccc',
                      color: '#000',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#b3b3b3'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ccc'}
                  >
                    No Show
                  </button>
                )}
                {effectivePermissions.includes('modifyBooking') && (
                  <button
                    onClick={() => setShowModifyModal(true)}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      backgroundColor: '#1976d2',
                      color: '#fff',
                      border: '1px solid #1976d2',
                      borderRadius: 4,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1565c0'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1976d2'}
                  >
                    Modify
                  </button>
                )}
                {effectivePermissions.includes('cancelBooking') && (
                  <button
                    onClick={handleCancel}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      backgroundColor: '#d32f2f',
                      color: '#fff',
                      border: '1px solid #d32f2f',
                      borderRadius: 4,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#b71c1c'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#d32f2f'}
                  >
                    Cancel
                  </button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      {showModifyModal && (
        <ModifyReservationModal
          booking={booking}
          open={showModifyModal}
          onClose={() => setShowModifyModal(false)}
          onSubmit={async (updatedDates) => {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/modify/${booking._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedDates)
              });

              const result = await response.json();
              if (result.success) {
                Swal.fire({
                  icon: 'success',
                  title: 'Reservation updated',
                  text: 'The check-in/check-out dates were successfully updated.',
                  confirmButtonColor: '#A58E63'
                }).then(() => {
                  window.location.reload();
                });
              } else {
                Swal.fire({
                  icon: 'error',
                  title: 'Failed to update',
                  text: result.message || 'An unknown error occurred.',
                  confirmButtonColor: '#A58E63'
                });
              }
            } catch (err) {
              Swal.fire({
                icon: 'error',
                title: 'Update error',
                text: err.message,
                confirmButtonColor: '#A58E63'
              });
            } finally {
              setShowModifyModal(false);
            }
          }}
        />
      )}
      
    </Box>
  );
};

export default BookingDetailView;